import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { OverlayPanel } from 'primereact/overlaypanel';
import { useArtists } from './hooks/useArtists';
import { useLabels } from './hooks/useLabels';
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { IFilters } from "./hooks/useTracks";
import { Button } from "ui-kit/Button";
import {FormattedMessage} from "react-intl";
import IntlStore from "stories/IntlStore";

export interface FiltersProps {
  filterArtistIds: string[];
  filterLabelIds: string[];
  trackTitle: string;
  isrc: string;
  onSelectFilter: (filter: IFilters) => void;
  onResetFilters: () => void;
}

export const Filters: React.FC<FiltersProps> = props => {
  const { filterArtistIds, isrc, filterLabelIds, trackTitle, onSelectFilter, onResetFilters } = props;
  const ref = useRef<OverlayPanel>(null);
  const [editFilterArtistIds, setEditFilterArtistIds] = useState<string[]>(filterArtistIds);
  const [editFilterLabelIds, setEditFilterLabelIds] = useState<string[]>(filterLabelIds);
  const [editTrackTitle, setEditTrackTitle] = useState(trackTitle);
  const [editISRC, setEditISRC] = useState(isrc);
  const { labels } = useLabels();
  const { artists } = useArtists();
  const isDisabledReset = filterArtistIds.length === 0 && filterLabelIds.length === 0 && trackTitle.length === 0 && isrc.length === 0;

  const onSave = () => {
    onSelectFilter({
      filterArtistIds: editFilterArtistIds,
      filterLabelIds: editFilterLabelIds,
      trackTitle: editTrackTitle,
      isrc: editISRC
    });
    ref.current?.hide();
  }

  const onReset = () => {
    setEditFilterArtistIds([]);
    setEditFilterLabelIds([]);
    setEditTrackTitle('');
    setEditISRC('');
    onResetFilters();
    ref.current?.hide();
  }

  return (
    <Container>
      <FiltersInput onClick={(e) => ref.current?.toggle(e)}>
        <FormattedMessage
          id="Tracks.Filters.TrackFiltersInput"
          defaultMessage="Track Filters"
        />
      </FiltersInput>
      <OverlayPanel ref={ref}>
        <FilterPanelContainer>
          <Row>
            <Label>
              <FormattedMessage
                id="Tracks.Filters.Artist"
                defaultMessage="Artist"
              />
            </Label>
            <StyledMultiSelect
              value={editFilterArtistIds}
              onChange={(e) => setEditFilterArtistIds(e.value)}
              options={artists.map(artist => ({ label: artist.name, value: artist.id }))}
              placeholder={IntlStore.intl.formatMessage({
                id: "Tracks.Filters.ArtistSelectArtist",
                defaultMessage: "Select the artist"
              })}
              filter
            />
          </Row>
          <Row>
            <Label>
              <FormattedMessage id="Tracks.Filters.TrackTitle" defaultMessage="Track title" />
            </Label>
            <InputText
              value={editTrackTitle}
              onChange={(e) => setEditTrackTitle(e.target.value)}
              placeholder={IntlStore.intl.formatMessage({
                id: "Tracks.Filters.SelectTrackTitle",
                defaultMessage: "Select the track title"
              })}
            />
          </Row>
          <Row>
            <Label>
              <FormattedMessage id="Tracks.Filters.Label" defaultMessage="Label" />
            </Label>
            <StyledMultiSelect
              value={editFilterLabelIds}
              onChange={(e) => setEditFilterLabelIds(e.value)}
              options={labels.map(label => ({ label: label.name, value: label.id }))}
              placeholder={IntlStore.intl.formatMessage({
                id: "Tracks.Filters.SelectLabel",
                defaultMessage: "Select the label"
              })}
              filter
            />
          </Row>
          <Row>
            <Label>ISRC</Label>
            <InputText
              value={editISRC}
              onChange={(e) => setEditISRC(e.target.value)}
              placeholder="Enter the ISRC"
            />
          </Row>
          <Actions>
            <ClearButton disabled={isDisabledReset} onClick={onReset}>
              <FormattedMessage id="Tracks.Filters.Clear" defaultMessage="Clear" />
            </ClearButton>
            <Button onClick={onSave}>
              <FormattedMessage id="Tracks.Filters.Save" defaultMessage="Save" />
            </Button>
          </Actions>
        </FilterPanelContainer>
      </OverlayPanel>
      <ResetButton onClick={onReset} $isDisabled={isDisabledReset}>
        <FormattedMessage id="Tracks.Filters.Reset" defaultMessage="Reset" />
      </ResetButton>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  height: 30px;
  gap: 5px;
`;

const FiltersInput = styled.div`
  width: 150px;
  height: 45px;
  background: #ffffff;
  transition: background-color 0.2s, color 0.2s, border-color 0.2s, box-shadow 0.2s;
  border-radius: 6px;
  outline-color: transparent;
  cursor: pointer;
  color: #6b7280;
  font-size: 16px;
  padding: 12px;
`;

const FilterPanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 360px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const Label = styled.div`
  color: #000000;
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
`;

const ResetButton = styled.div<{ $isDisabled?: boolean }>`
  cursor:  ${props => props.$isDisabled ? 'default': 'pointer'};
  color: ${props => props.$isDisabled ? 'rgba(0, 0, 0, 0.5)' : 'rgba(103, 119, 255, 1)'}
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
`;

const ClearButton = styled(Button)`
  background: none;
  border: none;
  color: #000000;
  font-weight: 600;
`;

const StyledMultiSelect = styled(MultiSelect)`
  width: 100%;
`;
